// client/src/components/tendermaker/BidStudioPage.js
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { tmBidList, tmBidAnalyze, tmGetRun, tmListRuns } from "./tendermakerApi";
import BidStudioPanel from "./BidStudioPanel";
import CollapsibleTopStrip from "./CollapsibleTopStrip";
import Layout from "../Layout";


const tiny = { color: "#6b7280", fontSize: 12 };

function sleep(ms) {
  return new Promise((r) => setTimeout(r, ms));
}

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

export default function BidStudioPage() {
  const navigate = useNavigate();
  const q = useQuery();
  const runIdFromUrl = q.get("runId") || "";
  const bidIdFromUrl = q.get("bidId") || "";

  const [bids, setBids] = useState([]);
  const [selectedBidId, setSelectedBidId] = useState(bidIdFromUrl);
  const [mode, setMode] = useState("auto");

  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState("");
  const [err, setErr] = useState("");
  const [run, setRun] = useState(null);

  // history
  const [history, setHistory] = useState([]);
  const [histBusy, setHistBusy] = useState(false);
  const [histCursor, setHistCursor] = useState(null);
  const [histHasMore, setHistHasMore] = useState(false);
  const [histStatusFilter, setHistStatusFilter] = useState("done");

  async function refreshBids() {
    const r = await tmBidList();
    setBids(r.data || []);
  }

  async function pollRun(runId) {
    for (;;) {
      const r = await tmGetRun(runId);
      setRun(r);
      setStatus(r.status || "");
      if (r.status === "done" || r.status === "error") return r;
      await sleep(2000);
    }
  }

  async function openRun(runId) {
    const r = await tmGetRun(runId);
    setRun(r);
    setStatus(r.status || "");
  }

  async function loadHistory({ reset = false, bidId = selectedBidId, statusFilter = histStatusFilter } = {}) {
    setHistBusy(true);
    try {
      const cursor = reset ? null : histCursor;
      const resp = await tmListRuns({
        bidId: bidId || undefined,
        status: statusFilter || undefined,
        limit: 20,
        cursor: cursor || undefined,
      });

      const items = resp?.data || [];
      const next = resp?.nextCursor || null;

      if (reset) setHistory(items);
      else setHistory((prev) => [...prev, ...items]);

      setHistCursor(next);
      setHistHasMore(Boolean(next));
    } catch (e) {
      console.error(e);
    } finally {
      setHistBusy(false);
    }
  }

  useEffect(() => {
    refreshBids().catch((e) => setErr(String(e?.message || e)));
  }, []);

  useEffect(() => {
    if (!runIdFromUrl) return;
    setBusy(true);
    setErr("");
    openRun(runIdFromUrl)
      .catch((e) => setErr(String(e?.message || e)))
      .finally(() => setBusy(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runIdFromUrl]);

  useEffect(() => {
    if (!selectedBidId) return;
    setErr("");
    setStatus("");
    setRun(null);

    (async () => {
      await loadHistory({ reset: true, bidId: selectedBidId, statusFilter: histStatusFilter });

      // Auto-open latest DONE run if not coming from URL
      if (!runIdFromUrl) {
        const resp = await tmListRuns({ bidId: selectedBidId, status: "done", limit: 1 });
        const latest = resp?.data?.[0];
        if (latest?.id) {
          navigate(
            `/tendermaker/bid-studio?bidId=${encodeURIComponent(selectedBidId)}&runId=${encodeURIComponent(latest.id)}`,
            { replace: true }
          );
        }
      }
    })().catch((e) => setErr(String(e?.message || e)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBidId]);

  async function runExtract() {
    if (!selectedBidId) return;
    setBusy(true);
    setErr("");
    setRun(null);
    setStatus("queued");
    try {
      const out = await tmBidAnalyze({ bidId: selectedBidId, mode });
      setStatus(out.status || "queued");

      navigate(`/tendermaker/bid-studio?bidId=${encodeURIComponent(selectedBidId)}&runId=${encodeURIComponent(out.runId)}`);
      await pollRun(out.runId);
      await loadHistory({ reset: true, bidId: selectedBidId, statusFilter: histStatusFilter });
    } catch (e) {
      setErr(String(e?.message || e));
    } finally {
      setBusy(false);
    }
  }

  const selectedBid = useMemo(() => bids.find((b) => b.id === selectedBidId), [bids, selectedBidId]);

  const latestRunId = useMemo(() => history?.[0]?.id || "", [history]);
  const currentRunId = run?.id || runIdFromUrl || "";

  return (
    <Layout>
    <div style={{ maxWidth: 1500, margin: "0 auto", padding: 14 }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap", alignItems: "baseline" }}>
        <h2 style={{ margin: 0 }}>TenderMaker — Bid Studio</h2>
        <div style={{ ...tiny }}>
          Evidence → jump → insert → attach source → draft → sign/stamp → merge
        </div>
      </div>

      {err ? <div style={{ color: "crimson", marginTop: 10, marginBottom: 10, fontSize: 12.5, fontWeight: 900 }}>{err}</div> : null}

      <CollapsibleTopStrip
        bids={bids}
        selectedBidId={selectedBidId}
        setSelectedBidId={setSelectedBidId}
        mode={mode}
        setMode={setMode}
        busy={busy}
        status={status}
        run={run}
        currentRunId={currentRunId}
        history={history}
        histBusy={histBusy}
        histHasMore={histHasMore}
        histStatusFilter={histStatusFilter}
        selectedBid={selectedBid}
        latestRunId={latestRunId}
        navigate={navigate}
        onRunExtract={runExtract}
        onRefreshBids={() => refreshBids().catch((e) => setErr(String(e)))}
        onOpenLatestDone={async () => {
          try {
            if (!selectedBidId) return;
            const resp = await tmListRuns({ bidId: selectedBidId, status: "done", limit: 1 });
            const latest = resp?.data?.[0];
            if (latest?.id) {
              navigate(
                `/tendermaker/bid-studio?bidId=${encodeURIComponent(selectedBidId)}&runId=${encodeURIComponent(latest.id)}`
              );
            }
          } catch {}
        }}
        onLoadHistory={({ reset = false } = {}) =>
          loadHistory({ reset, bidId: selectedBidId, statusFilter: histStatusFilter }).catch(() => {})
        }
        onOpenRun={openRun}
        onSetHistStatusFilter={(v) => {
          setHistStatusFilter(v);
          setHistCursor(null);
          setHistHasMore(false);
          setHistory([]);
          loadHistory({ reset: true, bidId: selectedBidId, statusFilter: v }).catch(() => {});
        }}
      />

      {/* Main work area */}
      {run ? <BidStudioPanel run={run} /> : null}
    </div>
    </Layout>
  );
}
